import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CreateFornecedorData, Fornecedor } from "@/hooks/useFornecedores";
import { formatAddressFromCep, formatAddressFromCnpj, lookupCep, lookupCnpj, maskCep, maskCnpj, maskPhone } from "@/lib/brasil";
import { useAutomaticBrazilLookup } from "@/hooks/useAutomaticBrazilLookup";
import { toast } from "sonner";

interface FornecedorFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFornecedorData) => Promise<boolean>;
  fornecedor?: Fornecedor | null;
  title: string;
}

export function FornecedorForm({ isOpen, onClose, onSubmit, fornecedor, title }: FornecedorFormProps) {
  const [formData, setFormData] = useState<CreateFornecedorData>({
    nome: fornecedor?.nome || "",
    cnpj: fornecedor?.cnpj || "",
    contato: fornecedor?.contato || "",
    telefone: fornecedor?.telefone || "",
    email: fornecedor?.email || "",
    endereco: fornecedor?.endereco || "",
    cep: fornecedor?.cep || "",
    categoria: fornecedor?.categoria || "",
    status: fornecedor?.status || "Ativo",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoCnpjEnabled, setAutoCnpjEnabled] = useState(false);
  const [autoCepEnabled, setAutoCepEnabled] = useState(false);

  useEffect(() => {
    setFormData({
      nome: fornecedor?.nome || "",
      cnpj: fornecedor?.cnpj || "",
      contato: fornecedor?.contato || "",
      telefone: fornecedor?.telefone || "",
      email: fornecedor?.email || "",
      endereco: fornecedor?.endereco || "",
      cep: fornecedor?.cep || "",
      categoria: fornecedor?.categoria || "",
      status: fornecedor?.status || "Ativo",
    });
    setAutoCnpjEnabled(false);
    setAutoCepEnabled(false);
  }, [fornecedor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const success = await onSubmit(formData);
    setIsSubmitting(false);
    if (success) onClose();
  };

  const handleChange = (field: keyof CreateFornecedorData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const cnpjLookup = useAutomaticBrazilLookup({
    value: formData.cnpj || "",
    digits: 14,
    enabled: autoCnpjEnabled,
    lookup: lookupCnpj,
    onSuccess: (info) => {
      setFormData((prev) => ({
        ...prev,
        cnpj: info.cnpj,
        nome: prev.nome || info.nome_fantasia || info.razao_social,
        contato: prev.contato || info.razao_social,
        telefone: prev.telefone || info.telefone,
        email: prev.email || info.email,
        cep: info.cep || prev.cep,
        endereco: formatAddressFromCnpj(info) || prev.endereco,
      }));
      toast.success("Dados do CNPJ preenchidos automaticamente");
    },
    onError: (error) => {
      toast.error(error.message || "Nao foi possivel buscar o CNPJ");
    },
  });

  const cepLookup = useAutomaticBrazilLookup({
    value: formData.cep || "",
    digits: 8,
    enabled: autoCepEnabled,
    lookup: lookupCep,
    onSuccess: (info) => {
      setFormData((prev) => ({ ...prev, cep: info.cep, endereco: prev.endereco || formatAddressFromCep(info) }));
      toast.success("CEP encontrado");
    },
    onError: (error) => {
      toast.error(error.message || "Nao foi possivel buscar o CEP");
    },
  });

  const handleCnpjLookup = () => void cnpjLookup.run();
  const handleCepLookup = () => void cepLookup.run();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div>
              <Label htmlFor="cnpj">CNPJ</Label>
              <Input id="cnpj" value={formData.cnpj} onChange={(e) => { setAutoCnpjEnabled(true); handleChange("cnpj", maskCnpj(e.target.value)); }} placeholder="00.000.000/0000-00" />
              {cnpjLookup.loading && <p className="text-xs text-muted-foreground">Consultando dados na Receita Federal...</p>}
            </div>
            <div className="flex items-end">
              <Button type="button" variant="outline" onClick={handleCnpjLookup} disabled={cnpjLookup.loading || !formData.cnpj}>
                {cnpjLookup.loading ? "Buscando..." : "Consultar novamente"}
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="nome">Nome da Empresa *</Label>
              <Input id="nome" value={formData.nome} onChange={(e) => handleChange("nome", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="contato">Pessoa de Contato *</Label>
              <Input id="contato" value={formData.contato} onChange={(e) => handleChange("contato", e.target.value)} required />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="telefone">Telefone *</Label>
              <Input id="telefone" value={formData.telefone} onChange={(e) => handleChange("telefone", maskPhone(e.target.value))} required />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} required />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div>
              <Label htmlFor="cep">CEP</Label>
              <Input id="cep" value={formData.cep || ""} onChange={(e) => { setAutoCepEnabled(true); handleChange("cep", maskCep(e.target.value)); }} placeholder="00000-000" />
              {cepLookup.loading && <p className="text-xs text-muted-foreground">Consultando CEP...</p>}
            </div>
            <div className="flex items-end">
              <Button type="button" variant="outline" onClick={handleCepLookup} disabled={cepLookup.loading || !formData.cep}>
                {cepLookup.loading ? "Buscando..." : "Consultar novamente"}
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="categoria">Categoria *</Label>
              <Select value={formData.categoria} onValueChange={(value) => handleChange("categoria", value)}>
                <SelectTrigger><SelectValue placeholder="Selecione uma categoria" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Materiais">Materiais Odontologicos</SelectItem>
                  <SelectItem value="Equipamentos">Equipamentos</SelectItem>
                  <SelectItem value="Laboratorio">Laboratorio</SelectItem>
                  <SelectItem value="Medicamentos">Medicamentos</SelectItem>
                  <SelectItem value="Limpeza">Produtos de Limpeza</SelectItem>
                  <SelectItem value="Escritorio">Material de Escritorio</SelectItem>
                  <SelectItem value="Outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status *</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="endereco">Endereco</Label>
            <Textarea id="endereco" value={formData.endereco} onChange={(e) => handleChange("endereco", e.target.value)} placeholder="Endereco completo do fornecedor" rows={3} />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1">{isSubmitting ? "Salvando..." : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
